import { useCallback } from 'react';

import { Box, Checkbox, Grid, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { FormikProvider, useFormik } from 'formik';

import { CissAutoComplete } from 'components/autocompletes/CissAutoComplete';
import { BbarForm } from 'components/docked/BbarForm';
import { CissNumberField, CissTextField } from 'components/fields';
import { Block } from 'components/page';
import { useLoaderContext } from 'context/LoaderContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useFocusErrorFormik } from 'hooks/useFocusErrorFormik';
import { useLojaDirty, useMutateColunasDinamicas, useQueryColunasDinamicas } from 'pages/cadastro/loja/hooks';
import { ColunaDinamicaProps } from 'pages/cadastro/loja/types';
import { allowNumberAndEspecialChars } from 'util/format';

const initialData = { lojaColunas: [] };

export function LojaColunasDinamicasForm() {
    const { hasPermission } = usePermissionsContext();
    const { data, isFetching } = useQueryColunasDinamicas();
    const { onSubmit, isLoading } = useMutateColunasDinamicas();
    const { focusError } = useFocusErrorFormik();

    useLoaderContext(isFetching || isLoading);

    const formik = useFormik({
        initialValues: data || initialData,
        validateOnChange: false,
        validateOnBlur: false,
        enableReinitialize: true,
        onSubmit,
    });

    const { values, setFieldValue, handleSubmit, dirty, errors, submitForm } = formik;

    focusError(errors);

    useLojaDirty(dirty);

    /**
     * Renderiza o campo de acordo com o tipo de configuração
     */
    const renderField = useCallback((coluna: ColunaDinamicaProps, index: number) => {
        const typeChave = coluna?.tipoConfiguracaoPropriedade?.tipoConfiguracaoPropriedadeEnum;

        switch (typeChave) {
            case 'OPCAO':
                return (
                    <CissAutoComplete
                        multiple={false}
                        options={coluna.configuracaoPropriedadeOpcaoList || []}
                        optionStringTemplate="{dsConfiguracaoPropriedadeOpcao}"
                        selectedStringTemplate="{dsConfiguracaoPropriedadeOpcao}"
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                        label={coluna.dsConfiguracao}
                        value={
                            coluna.configuracaoPropriedadeOpcaoList?.find(
                                (opcao) => String(opcao.idConfiguracaoPropriedadeOpcao) === String(coluna.dsValor),
                            ) || null
                        }
                        isOptionEqualToValue={(option, value) =>
                            option && option.idConfiguracaoPropriedadeOpcao === value.idConfiguracaoPropriedadeOpcao
                        }
                        onChange={(e, value) =>
                            setFieldValue(`lojaColunas.${index}.dsValor`, value ? String(value?.idConfiguracaoPropriedadeOpcao) : '')
                        }
                    />
                );

            case 'TEXTO':
                return (
                    <CissTextField
                        label={coluna.dsConfiguracao}
                        value={coluna.dsValor || ''}
                        onChange={(e) => setFieldValue(`lojaColunas.${index}.dsValor`, String(e.target.value))}
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                    />
                );

            case 'NUMERO_COM_CARACTERES_ESPECIAIS':
                return (
                    <CissTextField
                        type="text"
                        label={coluna.dsConfiguracao}
                        value={coluna?.dsValor || ''}
                        onInput={allowNumberAndEspecialChars}
                        onChange={(e) => setFieldValue(`lojaColunas.${index}.dsValor`, String(e.target.value))}
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                    />
                );

            case 'NUMERO':
                return (
                    <CissNumberField
                        inputType="number"
                        label={coluna.dsConfiguracao}
                        value={coluna.dsValor || ''}
                        onChange={(e) => setFieldValue(`lojaColunas.${index}.dsValor`, String(e.target.value))}
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                    />
                );

            case 'DATA':
                return (
                    <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                        <DatePicker
                            value={coluna?.dsValor ? new Date(coluna?.dsValor) : null}
                            label={coluna?.dsConfiguracao}
                            format="dd/MM/yyyy"
                            sx={{ width: '100%' }}
                            onChange={(value) => setFieldValue(`lojaColunas.${index}.dsValor`, value?.toISOString())}
                            disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                            slotProps={{
                                textField: {
                                    name: coluna?.dsChave,
                                    label: coluna.dsConfiguracao,
                                },
                            }}
                        />
                    </LocalizationProvider>
                );

            case 'HORA':
                return (
                    <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                        <TimePicker
                            value={coluna.dsValor ? new Date(coluna.dsValor) : null}
                            onChange={(value) => setFieldValue(`lojaColunas.${index}.dsValor`, value?.toISOString())}
                            sx={{ width: '100%' }}
                            disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                            slotProps={{
                                textField: {
                                    name: coluna?.dsChave,
                                    label: coluna.dsConfiguracao,
                                },
                            }}
                        />
                    </LocalizationProvider>
                );

            case 'BOOLEANO':
                return (
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: '6px', pl: '10px' }}>
                        <Typography>{coluna.dsConfiguracao}</Typography>
                        <Checkbox
                            checked={String(coluna.dsValor) === 'true'}
                            onChange={(e) => setFieldValue(`lojaColunas.${index}.dsValor`, String(e.target.checked))}
                            disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                        />
                    </Box>
                );

            default:
                return (
                    <CissTextField
                        value={coluna.dsValor || ''}
                        onChange={(e) => setFieldValue(`lojaColunas.${index}.dsValor`, String(e.target.value))}
                        disabled={!hasPermission(['SECAO_DADOS_ALTERAR'])}
                    />
                );
        }
    }, []);

    if (isFetching) return null;

    return (
        <FormikProvider value={formik}>
            <Block>
                <Grid container spacing={2} sx={{ width: '100%' }}>
                    {values.lojaColunas.map((coluna, index) => (
                        <Grid key={coluna?.idConfiguracaoPropriedade || index} size={{ xs: 12, sm: 6, md: 4 }}>
                            {renderField(coluna, index)}
                        </Grid>
                    ))}
                </Grid>
            </Block>

            <BbarForm submitForm={submitForm} />
        </FormikProvider>
    );
}
